import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { portalApi } from '../api/portalApi';
import { setTbdList } from './tbdApiSlice';
import { setPumpDetails } from './pumpDetailsSlice';
import { setChangeOrderDetails } from './changeOrderDetailsSlice';
import { setSellPricesFromRecords } from './sellPriceSlice';

// Re-export RTK Query hook for backward compatibility
export { useGetJobListQuery } from '../api/portalApi';

/**
 * Fetches the full schedule from /full-schedule?page=1.
 * Normalizes the keyed response object into flat arrays and populates
 * the TBD, pump details, sold-to/ship-to (sell price), and
 * change order details slices in one request.
 */
export const normalizeFullSchedulePayload = (rawData) => {
  const normalized = {
    jobs: [],
    tbdList: [],
    pumpDetails: [],
    soldToShipTo: [],
    changeOrderDetails: [],
  };

  Object.values(rawData || {}).forEach((item) => {
    const generalProjectInfo = item?.GeneralProjectInfo ?? item?.generalProjectInfo;
    const tbdAudit = item?.TBDAudit ?? item?.tbdAudit;
    const projectPumpDetails = item?.PumpDetails ?? item?.pumpDetails;
    const projectSoldToShipTo = item?.SoldToShipTo ?? item?.soldToShipTo;
    const projectChangeOrders = item?.ChangeOrderDetails ?? item?.changeOrderDetails;

    if (generalProjectInfo) normalized.jobs.push({ ...generalProjectInfo, SoldToShipTo: projectSoldToShipTo ?? [] });
    if (Array.isArray(tbdAudit)) normalized.tbdList.push(...tbdAudit);
    if (Array.isArray(projectPumpDetails)) normalized.pumpDetails.push(...projectPumpDetails);
    if (Array.isArray(projectSoldToShipTo)) normalized.soldToShipTo.push(...projectSoldToShipTo);
    if (Array.isArray(projectChangeOrders)) normalized.changeOrderDetails.push(...projectChangeOrders);
  });

  return normalized;
};

// Foreground readers use the RTK query; background readers use portalApi's
// fullScheduleRequest builder to preserve the same filtered first page.
export const readFullScheduleSnapshot = async (dispatch, url) => {
  const request = dispatch(portalApi.endpoints.getFullSchedule.initiate(
    url,
    { forceRefetch: true, subscribe: false }
  ));
  try {
    return await request.unwrap();
  } finally {
    request.unsubscribe?.();
  }
};

export const fetchFullSchedule = createAsyncThunk(
  'jobSchedule/fetchFullSchedule',
  async (_, { dispatch, getState }) => {
    const apiUrls = getState().apiUrls.urls;
    // portalApi unwraps the API's top-level data envelope, leaving the
    // project-keyed aggregate expected by the schedule normalizer.
    const rawData = await readFullScheduleSnapshot(dispatch, apiUrls.fullSchedule);
    const {
      jobs,
      tbdList,
      pumpDetails,
      soldToShipTo,
      changeOrderDetails,
    } = normalizeFullSchedulePayload(rawData);

    dispatch(setTbdList(tbdList));
    dispatch(setPumpDetails(pumpDetails));
    dispatch(setSellPricesFromRecords(soldToShipTo));
    dispatch(setChangeOrderDetails(changeOrderDetails));

    return jobs;
  }
);

/**
 * Backward-compatible thunk wrapper.
 * dispatch(fetchJobSchedule()) continues to work for all existing callers.
 * The filters parameter is accepted but unused — the full-schedule endpoint
 * handles all jobs in one response.
 */
export const fetchJobSchedule = (filters = null) => (dispatch) => {
  return dispatch(fetchFullSchedule());
};

/**
 * Backward-compatible thunk wrapper for clearing job schedule state.
 */
export const clearJobSchedule = () => (dispatch) => {
  return dispatch(jobScheduleSlice.actions.clear());
};

const jobScheduleSlice = createSlice({
  name: 'jobSchedule',
  initialState: {
    data: [],
    loading: false,
    error: null,
  },
  reducers: {
    replaceRealtimeSchedule: (state, action) => {
      state.data = action.payload;
    },
    clear: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
    },
    updateJob: (state, action) => {
      const updatedFields = action.payload;
      const jobIndex = state.data.findIndex(job => job.ID === updatedFields.ID);
      if (jobIndex !== -1) {
        state.data[jobIndex] = {
          ...state.data[jobIndex],
          ...updatedFields
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFullSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFullSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchFullSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message ?? 'Failed to fetch job schedule';
      });
  },
});

export const { updateJob, replaceRealtimeSchedule } = jobScheduleSlice.actions;
export default jobScheduleSlice.reducer;
